import { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A dialog: white, 8px radius, serif title, one sentence saying what will happen (`why`), then the fields, then
 * the footer with the main action first. Drawn on document.body so nothing on the page (the sticky top bar, a
 * section's stacking) can sit over it. The title and the footer stay put; only the fields scroll. On a phone it
 * keeps 12px from every edge and fits the visible screen (dvh), so the browser's own bars never hide its title.
 * Fades and rises in; nothing moves for people who ask for reduced motion. Traps focus and gives it back.
 */
export default function Modal({
  title,
  why,
  onClose,
  children,
  footer,
  width = 560,
}: {
  title: string;
  why?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const el = box.current;
    if (el && !el.contains(document.activeElement)) {
      const first = el.querySelector<HTMLElement>('[autofocus]') ?? el.querySelector<HTMLElement>(FOCUSABLE);
      // On a phone, focusing a field opens the keyboard and jumps the page: focus the dialog itself there.
      (window.matchMedia('(max-width: 767px)').matches ? el : (first ?? el)).focus({ preventScroll: true });
    }
    const body = document.body.style;
    const overflow = body.overflow;
    body.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      if (e.key !== 'Tab' || !box.current) return;
      const items = Array.from(box.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      body.overflow = overflow;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6 max-md:items-end max-md:p-3 max-md:pt-[max(12px,env(safe-area-inset-top))] max-md:pb-[max(12px,env(safe-area-inset-bottom))] motion-safe:animate-fade-in"
      style={{ background: 'rgba(42,36,32,.45)' }}
      onClick={onClose}
    >
      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{ width }}
        className="flex flex-col bg-surface text-ink-2 border border-line rounded-md shadow-lift max-w-full max-h-[calc(100vh-48px)] supports-[height:100dvh]:max-h-[calc(100dvh-48px)] text-[15px] leading-[22px] outline-none max-md:w-full max-md:max-h-[calc(100vh-24px)] max-md:supports-[height:100dvh]:max-h-[calc(100dvh-24px)] motion-safe:animate-dialog-in max-md:motion-safe:animate-sheet-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-7 pt-7 max-md:px-4 max-md:pt-5 flex-none">
          <h2 className="font-serif font-semibold text-[24px] leading-8 text-ink max-md:text-[22px] max-md:leading-[30px]">
            {title}
          </h2>
          {why ? <p className="mt-[6px] text-ink-2">{why}</p> : null}
        </div>
        <div className="px-7 pt-5 pb-1 max-md:px-4 overflow-y-auto overscroll-contain min-h-0 flex-1">{children}</div>
        {footer ? (
          <div className="dialog-foot flex-none flex flex-wrap items-center gap-3 px-7 pt-4 pb-7 max-md:px-4 max-md:pb-4 border-t border-line mt-4 max-md:[&>button]:flex-[1_1_100%]">
            {footer}
          </div>
        ) : (
          <div className="flex-none h-7 max-md:h-4" />
        )}
      </div>
    </div>,
    document.body,
  );
}
